import { chain } from '@aibees/shared/utils/chain';
import {
  cloneDeep,
  each,
  filter,
  find,
  isEqual,
  lowerCase,
  map,
} from 'lodash-es';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useBoolean } from '@aibees/shared/hooks';
import { fOrdinal } from '@aibees/shared/utils';
import {
  EXPORT_TEMPLATE_MAX_LIMIT,
  EXPORT_TEMPLATE_MAX_LIMIT_FOR_INTEGRATION,
} from '../constant';
import { useHasChangesFields } from '../hooks/use-has-changes-fields';
import {
  EditData,
  TemplateCategory,
  TemplateInformation,
  TransferredData,
} from '../types';
import { updateDraftItemsBasedOnEditValue } from '../utils';
import {
  TemplateContextData as ContextData,
  TemplateContextProps as ContextProps,
} from './types';

const context = createContext<ContextData>({} as ContextData);

export const { Provider } = context;

export const useTemplateContext = () => useContext(context);

export const TemplateProvider = ({
  children,
  integrationConfig,
}: ContextProps) => {
  const [defaultItems, setDefaultItems] = useState<TemplateCategory[]>([]);
  const [draftItems, setDraftItems] = useState<TemplateCategory[]>([]);
  const [information, setInformation] = useState<TemplateInformation>();
  const [transferredData, setTransferredData] = useState<TransferredData[]>([]);
  const [templateName, setTemplateName] = useState<string>('');
  const [editValue, setEditValue] = useState<EditData>();
  const showLimit = useBoolean(false);
  const [isLoading, setIsLoading] = useState(false);
  const exportTemplateMaxLimit = useMemo(() => {
    return integrationConfig
      ? EXPORT_TEMPLATE_MAX_LIMIT_FOR_INTEGRATION
      : EXPORT_TEMPLATE_MAX_LIMIT;
  }, [integrationConfig]);

  const isResetDisabled = useMemo(() => {
    return isEqual(draftItems, defaultItems);
  }, [draftItems, defaultItems]);

  const hasChanges = useHasChangesFields(transferredData, editValue);

  const handleInitEditData = (data: EditData) => {
    setEditValue(data);
  };

  const handleEnterName = (value: string) => {
    setTemplateName(value);
  };
  const handleInitItems = (
    items: TemplateCategory[],
    info?: TemplateInformation
  ) => {
    // Add a permanent bottom entry to each category (except if isSingleEntry is true)
    const itemsWithBottomEntry = items.map((category) => {
      // Skip adding bottom entry if isSingleEntry is true
      if (category.isSingleEntry) {
        return category;
      }

      const bottomEntry = {
        name: 'Bottom Entry',
        isBottom: true,
        notAdded: true,
        checked: false,
        fields:
          category.entries[0]?.fields?.map((field: any) => ({
            ...field,
            selected: false,
          })) || [],
      };

      const entries = editValue?.value
        ? category.entries
        : [...category.entries, bottomEntry];

      return {
        ...category,
        entries,
      };
    });

    setDefaultItems(itemsWithBottomEntry);
    setDraftItems(itemsWithBottomEntry);
    setInformation(info);
  };

  const createToggleOptionSelection =
    (categoryName: string) => (entryName: string) => (optionName: string) => {
      const updatedItems = cloneDeep(draftItems);
      const category = updatedItems.find((cat) => cat.name === categoryName);
      const entry = category?.entries.find((ent) => ent.name === entryName);
      const option = entry?.fields.find((field) => field.name === optionName);

      if (entry && !entry?.checked) {
        entry.checked = true;
      }

      if (!option) return;

      const isCurrentlySelected = option.selected;
      const total = getTotalSelectedOptionsCount();

      if (!isCurrentlySelected && total >= exportTemplateMaxLimit) {
        showLimit.onTrue();
        return;
      }

      option.selected = !isCurrentlySelected;

      setDraftItems(updatedItems);

      if (isCurrentlySelected && total <= exportTemplateMaxLimit) {
        showLimit.onFalse();
      }

      if (total <= exportTemplateMaxLimit) {
        showLimit.onFalse();
      }
    };

  const selectAllOptions =
    (categoryName: string) => (entryName: string) => () => {
      const updatedItems = cloneDeep(draftItems);
      const category = updatedItems.find((cat) => cat.name === categoryName);
      const entry = category?.entries.find((ent) => ent.name === entryName);
      const fields = entry?.fields;

      if (!fields) return;

      const totalSelectedNow = getTotalSelectedOptionsCount();
      // Coming-soon fields aren't selectable individually (they render a badge
      // instead of a toggle), so Select All must skip them too — otherwise it
      // silently selects a not-yet-available column (e.g. FOLLOWER_COUNT) that
      // then gets persisted into the template and rejected by the backend.
      const unselectedCount = fields.filter(
        (f) => !f.selected && !f.disabled && !f.comingSoon
      ).length;
      const totalIfAllSelected = totalSelectedNow + unselectedCount;

      if (totalIfAllSelected > exportTemplateMaxLimit) {
        showLimit.onTrue();
        return;
      }

      showLimit.onFalse();
      fields.forEach((f) => {
        if (!f.disabled && !f.comingSoon) {
          f.selected = true;
        }
      });

      if (entry && !entry?.checked) {
        entry.checked = true;
      }

      setDraftItems(updatedItems);
    };

  const deselectAllOptions =
    (categoryName: string) => (entryName: string) => () => {
      const updatedItems = cloneDeep(draftItems);
      const category = updatedItems.find((cat) => cat.name === categoryName);
      const entry = category?.entries.find((ent) => ent.name === entryName);
      const fields = entry?.fields;

      if (!fields) return;

      fields.forEach((f) => {
        if (!f.disabled) {
          f.selected = false;
        }
      });

      // Only uncheck entry if there are no selected fields remaining (including disabled ones)
      const hasSelectedFields = fields.some((f) => f.selected);
      if (entry && entry?.checked && !hasSelectedFields) {
        entry.checked = false;
      }

      showLimit.onFalse();

      setDraftItems(updatedItems);
    };

  const toggleEntryChecked = (categoryName: string) => (entryName: string) => {
    const updatedItems = cloneDeep(draftItems);
    const updatedDefaultItems = cloneDeep(defaultItems);

    // Find the entry in draft items
    const draftCategory = chain(updatedItems)
      .find({ name: categoryName })
      .value();
    const draftEntry = draftCategory?.entries.find(
      (ent: any) => ent.name === entryName
    );

    // Find the corresponding entry in default items
    const defaultCategory = chain(updatedDefaultItems)
      .find({ name: categoryName })
      .value();
    const defaultEntry = defaultCategory?.entries.find(
      (ent: any) => ent.name === entryName
    );

    if (draftEntry) {
      // Toggle the checked state in draft items
      draftEntry.checked = !draftEntry.checked;

      // If the entry is being checked and defaultEntry exists, deselect all selected options in default items
      if (draftEntry.checked && defaultEntry) {
        defaultEntry.fields?.forEach((field: any) => {
          if (field.selected && !field.disabled) {
            field.selected = false;
          }
        });
      }
    }

    setDraftItems(updatedItems);
    setDefaultItems(updatedDefaultItems);
  };

  const handleActiveEntry = (categoryName: string) => (entryName: string) => {
    const updatedItems = cloneDeep(draftItems);

    const category = find(updatedItems, { name: categoryName });
    const entry = category?.entries.find((ent: any) => ent.name === entryName);

    if (entry && category) {
      // Find the last active entry (not notAdded) to copy fields from
      const lastActiveEntry = category.entries
        .filter((ent: any) => !ent.notAdded)
        .slice(-1)[0];

      if (lastActiveEntry) {
        // Copy fields from the last active entry
        entry.fields = cloneDeep(lastActiveEntry.fields);
      }

      // Set notAdded to false
      entry.notAdded = false;
      entry.checked = true;
    }

    setDraftItems(updatedItems);
  };

  const handleClear = (categoryName: string) => {
    const updatedItems = cloneDeep(draftItems);

    const category = find(updatedItems, { name: categoryName });
    if (category) {
      each(category.entries, (entry) => {
        each(entry.fields, (option) => {
          if (!option.disabled) {
            option.selected = false;
          }
        });

        // Only uncheck entry if there are no selected fields remaining (including disabled ones)
        const hasSelectedFields = entry.fields?.some((f: any) => f.selected);
        if (entry && entry?.checked && !hasSelectedFields) {
          entry.checked = false;
        }
      });
    }
    setDraftItems(updatedItems);
    showLimit.onFalse();
  };

  const handleClearAllCategories = () => {
    const updatedItems = cloneDeep(draftItems);

    each(updatedItems, (category) => {
      each(category.entries, (entry) => {
        each(entry.fields, (option) => {
          if (!option.disabled) {
            option.selected = false;
          }
        });

        // Only uncheck entry if there are no selected fields remaining (including disabled ones)
        const hasSelectedFields = entry.fields?.some((f: any) => f.selected);
        if (entry && entry?.checked && !hasSelectedFields) {
          entry.checked = false;
        }
      });
    });

    setDraftItems(updatedItems);
  };

  const handleRemoveEntry = (categoryName: string) => (entryName: string) => {
    const updatedItems = cloneDeep(draftItems);

    const category = find(updatedItems, { name: categoryName });
    const entry = category?.entries.find((ent: any) => ent.name === entryName);

    if (entry) {
      // Set notAdded to true
      entry.notAdded = true;

      if (entry?.checked) {
        entry.checked = false;
      }

      // Clear all selected options in this entry
      entry.fields?.forEach((field: any) => {
        if (!field.disabled) {
          field.selected = false;
        }
      });
    }

    setDraftItems(updatedItems);
  };

  const getSelectedOptionsCount = (categoryName: string): number => {
    const category = chain(draftItems).find({ name: categoryName }).value();
    if (!category) return 0;

    return category.entries.reduce(
      (
        acc: any,
        entry: {
          checked: any;
          notAdded: any;
          fields: {
            filter: (arg0: (option: any) => any) => {
              (): any;
              new (): any;
              length: any;
            };
          };
        }
      ) => {
        if (entry.checked && !entry.notAdded) {
          const selectedCount = entry.fields?.filter(
            (option: { selected: any }) => option.selected
          ).length;
          return acc + selectedCount;
        }
        return acc;
      },
      0
    );
  };

  const handleTransfer = (oldState: TemplateCategory[]): TransferredData[] => {
    return map(oldState, (category) => {
      const checkedEntries = filter(category.entries, 'checked');
      const bottomEntries = filter(category.entries, { isBottom: true });

      // Check if any bottom entries have selected fields
      const bottomEntriesWithSelectedFields = bottomEntries.filter(
        (bottomEntry) => bottomEntry.fields.some((field) => field.selected)
      );

      // Always include the first entry (index 0) even if it's not checked
      const firstEntry = category.entries[0];
      const firstEntryIsChecked = checkedEntries.some(
        (entry) => entry.name === firstEntry?.name
      );

      // Include both checked entries and bottom entries
      const entriesToInclude = [
        // Add first entry if it exists and is not already in checkedEntries
        ...(firstEntry && !firstEntryIsChecked ? [firstEntry] : []),
        ...checkedEntries,
        ...bottomEntries.filter(
          (bottomEntry) =>
            !checkedEntries.some(
              (checkedEntry) => checkedEntry.name === bottomEntry.name
            )
        ),
      ];

      return {
        key: category.key,
        categoryName: lowerCase(category.name),
        disabled: category.disabled,
        isSingleEntry: category.isSingleEntry,
        entries: map(entriesToInclude, (entry, index) => ({
          index: entry.isBottom ? 1000000 : index,
          name: entry.name,
          checked: entry.checked,
          isBottom: entry.isBottom || false,
          notAdded:
            (entry.isBottom && bottomEntriesWithSelectedFields.length === 0) ||
            false,
          fields: map(filter(entry.fields, 'selected'), (field) => field.type),
        })),
      };
    });
  };

  const handleAddEntry = (categoryName: string) => {
    const total = getTotalSelectedOptionsCount();
    const newState = cloneDeep(draftItems);
    const category = find(newState, { name: categoryName });
    const newEntryIndex =
      category && category.entries.filter((i) => !i.isBottom).length + 1;

    if (category) {
      // Find the last non-bottom entry to clone from
      const lastNonBottomEntry = category.entries
        .filter((entry: any) => !entry.isBottom)
        .slice(-1)[0];

      if (lastNonBottomEntry) {
        const newEntry = cloneDeep(lastNonBottomEntry);
        newEntry.name = `${fOrdinal(newEntryIndex || 0 + 1)} Entry`;
        newEntry.notAdded = false;
        newEntry.isBottom = false;

        // Find the position to insert (before the first bottom entry)
        const firstBottomIndex = category.entries.findIndex(
          (entry: any) => entry.isBottom
        );
        if (firstBottomIndex === -1) {
          // No bottom entries, add to end
          category.entries.push(newEntry);
        } else {
          // Insert before the first bottom entry
          category.entries.splice(firstBottomIndex, 0, newEntry);
        }

        const totalEntry = newEntry.fields.length + total;
        if (totalEntry <= exportTemplateMaxLimit) {
          showLimit.onFalse();
          setDraftItems(newState);
        }

        if (totalEntry > exportTemplateMaxLimit) {
          showLimit.onTrue();
          return;
        }
      }
    }
  };

  const handleReset = () => {
    setDraftItems(defaultItems);
  };

  const getSelectedOptionsCountForEntry =
    (categoryName: string) =>
    (entryName: string): number => {
      const category = chain(draftItems).find({ name: categoryName }).value();
      if (!category) return 0;

      const entry = find(category.entries, { name: entryName });
      if (!entry) return 0;

      const selectedCount = filter(entry.fields, { selected: true }).length;
      return selectedCount;
    };

  const getTotalSelectedOptionsCount = (): number => {
    return draftItems?.reduce((totalCount, category) => {
      return totalCount + getSelectedOptionsCount(category.name);
    }, 0);
  };

  const updateDraftWithEditValue = () => {
    if (!editValue) {
      setIsLoading(true);
    } else {
      const updated = updateDraftItemsBasedOnEditValue(
        editValue.value,
        defaultItems
      );
      setDraftItems(updated);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const data = handleTransfer(draftItems);
    setTransferredData(data);
  }, [draftItems]);

  return (
    <Provider
      value={{
        handleInitItems,
        createToggleOptionSelection,
        selectAllOptions,
        deselectAllOptions,
        toggleEntryChecked,
        getSelectedOptionsCount,
        getSelectedOptionsCountForEntry,
        updateDraftWithEditValue,
        handleInitEditData,
        handleAddEntry,
        totalSelectedOptions: getTotalSelectedOptionsCount(),
        handleClearAllCategories,
        handleRemoveEntry,
        items: draftItems,
        handleActiveEntry,
        transferredData,
        handleEnterName,
        templateName,
        handleClear,
        handleReset,
        editValue,
        showLimit,
        information,
        isLoading,
        isResetDisabled,
        hasChanges,
      }}
    >
      {children}
    </Provider>
  );
};
